<script lang="ts">
  import { _ } from "svelte-i18n";
  import Price from "$lib/components//Price/Price.svelte";
  import Typography from "$lib/components//Typography/Typography.svelte";
  import { ImageConfig3Card } from "$lib/components/Cards/ProductCard/ProductCard.image-config";
  import type {
    Image,
    SelectedOptionInput as Option,
    MoneyV2,
  } from "$lib/gql/gen/codegen";
  import type { PriceRange } from "$lib/gql/genCms/codegen";
  import { linkTo } from "$lib/utils/url";
  import type { colourVariant } from "$lib/data/colours.data";
  import { goto } from "$app/navigation";
  import Picture from "$lib/components/Picture/Picture.svelte";

  export let title: string;
  export let subtitle: string;
  export let priceRange: PriceRange;
  export let comparePriceRange: PriceRange;
  export let image: Image;
  export let url: string;
  export let showPrice = true;
  export let paddingLg = false;
  export let imgBorder = false;
  export let selectedColourOption: colourVariant = null;
  export let imageConfig = ImageConfig3Card;
  export let price: MoneyV2;
  export let comparePrice: MoneyV2;
  export let eager: boolean = false;

  let viewportWidth: number;
  let isMobile = false;
  let lgWidth = 1280;

  $: isMobile = viewportWidth < lgWidth;
</script>

<style lang="scss">
  .product-card:hover .product-card--img:not(.tray-open) {
    transform: scale(1.08);
    transform-origin: center;
  }

  :global(.product-card--img-wrapper .product-card--img .inner.coverImage) {
    overflow: visible;
  }

  :global(.product-card--img-wrapper > .product-card--img > .inner) {
    aspect-ratio: 4/5;
  }
  :global(.product-card--img-wrapper__mobile) {
    display: flex !important;
    align-items: center;
  }

  .product-card {
    width: 100%;
    display: flex;
    position: relative;

    &--anchor {
      position: static;
      text-decoration: none;
      justify-content: space-between;
      display: flex;
      align-items: self-end;

      /* make the anchor tag cover the entire product card area */
      &:before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 3;
      }
    }
    &--badge {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
    }
    &--img {
      position: relative;
      aspect-ratio: 4/5;
      &-wrapper {
        aspect-ratio: 4/5;
        display: block;
        position: relative;
        overflow: hidden;
        max-width: 100px;

        &__border {
          border-bottom: 0.5px solid $background;
        }
      }
      transition: 300ms ease-in-out;
    }
    &--info {
      background-color: $surface;
      position: relative;
      padding: 8px;
      width: 100%;

      text-decoration: none;

      &__padded-lg {
        padding: 16px;
      }
      &__title {
        text-transform: capitalize;
      }
      &__price {
        margin-top: 8px;
      }
    }

    &--quick-buy {
      &__container {
        position: absolute;
        z-index: 5;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        pointer-events: none;
        bottom: 0px;
      }

      &__button {
        display: flex;
        justify-content: center;
        align-items: center;
        pointer-events: all;
        position: relative;
        width: 14px;
        height: 14px;
        padding: 20px;
        margin: 16px;
        background-color: $inverse-on-surface-variant-60;
        color: $on-background;
        border: 1px solid $on-surface-10;
        border-radius: 50%;
      }
    }

    &--add-to-bag {
      border-top: 1px solid $background;
      padding: 16px;

      @include retina {
        border-top: 0.5px solid $background;
      }

      @include breakpoint(md) {
        padding: 24px 44px;
      }
    }
  }

  .quick-buy-colours {
    padding-top: 16px;
  }

  .tray-wrapper {
    &--add-to-bag {
      padding: 24px 16px 16px;
    }

    :global(.option-container .option-groups) {
      width: 100%;
    }
  }

  .back-in-stock-wrapper {
    padding: 24px 24px 16px;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div
  class="product-card"
  on:click|preventDefault|stopPropagation
  on:keydown|preventDefault|stopPropagation
  on:mousedown|preventDefault|stopPropagation
  role="button"
  tabindex="0"
>
  <span class="sr-only">{title}</span>
  <div
    class={`product-card--img-wrapper ${
      isMobile ? "product-card--img-wrapper__mobile" : ""
    } ${imgBorder && "product-card--img-wrapper__border"}`}
  >
    <div class="product-card--anchor">
      <div class="product-card--img">
        {#if image && image.originalSrc}
          <Picture
            smImage={image.originalSrc}
            mdImage={image.originalSrc}
            {imageConfig}
            altText={image.altText}
            cover
            shopifyImage
            {eager}
          />
        {/if}
      </div>
    </div>
  </div>

  {#if title || subtitle || priceRange}
    <div
      class={`product-card--info ${
        paddingLg && "product-card--info__padded-lg"
      }`}
    >
      <a class="product-card--anchor" href={linkTo(url)}>
        <div class="product-card--info__base">
          <div class="product-card--info__title">
            <Typography component="span" variant={"button1"}
              >{title.toLowerCase() || ""}</Typography
            >
          </div>
          <div class="product-card--info__subtitle">
            <Typography component="span" variant="body2" theme="on-surface"
              >{subtitle || ""} {selectedColourOption.identifier}</Typography
            >
          </div>
        </div>

        {#if showPrice}
          {#if priceRange && priceRange.minVariantPrice && priceRange.minVariantPrice.amount}
            <div class="product-card--info__price">
              <Price
                variantPrice={price}
                {priceRange}
                variantComparePrice={comparePrice}
                {comparePriceRange}
                variant="body2"
              />
            </div>
          {/if}
        {/if}
      </a>
    </div>
  {/if}
</div>
